import { useState } from 'react';
import _ from 'lodash';

import { useSelector, useDispatch } from 'react-redux';

import {
  getCameraAccess,
  getGeolocation,
  geolocationSupported,
  logErrorToServer,
  CustomError,
  requestGyroscopePermission,
} from 'utils';

import { PhotoSeries } from 'redux/photoseries/photoseries';
import { getPhotoSeries } from 'redux/photoseries/photoseries.selectors';

import { makeGet, makePatch } from 'services';
import { AjaxResponse } from 'rxjs/ajax';

import {
  setGeolocationAccessNeeded,
  setGeolocationEnabled,
  setServiceUnavailable,
  setCameraAccessDenied,
  storeUseNativeCamera
} from 'redux/root.actions';

import { selectUseNativeCamera } from 'redux/root.selectors';

export const useCameraAndGPSAccess = () => {
  const [isAccessGrantingInProgress, setIsAccessGrantingInProgress] = useState(false);
  const [isAccessGranted, setIsAccessGranted] = useState(false);

  const dispatch = useDispatch();

  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);
  const useNativeCamera = useSelector(selectUseNativeCamera);

  const setGpsState = (gpsAccessNeeded: boolean, gpsEnabled: boolean) => {
    dispatch(setGeolocationAccessNeeded(gpsAccessNeeded));
    dispatch(setGeolocationEnabled(gpsEnabled));
  };

  const getGeolocationAccess = () => {
    // If GPS is not enabled at all, or is optional and not supported, do nothing
    if (!photoSeries.trackGeolocationEnabled || (!photoSeries.trackGeolocationRequired && !geolocationSupported)) {
      setGpsState(false, false);
      setIsAccessGranted(true);
      return;
    }

    // If expected country geolocation is required, then hard block if GPS not required or GPS not supported
    if (photoSeries.expectedCountryGeolocationRequired && (!photoSeries.trackGeolocationRequired || !geolocationSupported)) {
      setGpsState(true, false);
      return;
    }

    // If GPS is required and geolocation is not supproted, then hard block
    if (photoSeries.trackGeolocationRequired && !geolocationSupported) {
      setGpsState(true, false);
      return;
    }

    // No need to save GPS if only capturing additional images, as it is already done with captured series
    if (photoSeries.isCaptureOnlyPhotosOfAdditionalImages) {
      setGpsState(false, false);
      setIsAccessGranted(true);
      return;
    }

    // Manual timeout fallback in case browser geolocation never calls back
    const fallbackTimeout = 8000;
    let didRespond = false;

    const finalize = () => {
      didRespond = true;
      clearTimeout(timer);
    };

    const handleFailure = (message: string) => {
      logErrorToServer(message, null);
      setGpsState(photoSeries.trackGeolocationRequired, false);

      // If GPS isn’t required, allow access anyway
      if (!photoSeries.trackGeolocationRequired) {
        setIsAccessGranted(true);
      }
    };

    // Some browsers or environments may define navigator.geolocation but not implement its methods,
    // or the methods may be stubbed and do nothing (e.g., in some headless browsers, privacy modes,
    // or restricted environments). In such cases, getCurrentPosition may not call the callbacks or may throw errors,
    // even though navigator.geolocation is defined. Always handle errors and timeouts defensively.
    const timer = setTimeout(() => {
      if (didRespond) return;
      finalize();

      const msg = `GeolocationPositionError (manual timeout fallback); GPS Required: ${photoSeries.trackGeolocationRequired}`;
      handleFailure(msg);
    }, fallbackTimeout);

    getGeolocation(
      // Success callback
      ({ coords: { latitude, longitude, accuracy } }) => {
        if (didRespond) return;
        finalize();

        // Save coordinates
        makePatch(`capture/${photoSeries.id}/location`, {
          latitude,
          longitude,
          accuracy: accuracy || null
        }).subscribe(() => {
          if (!photoSeries.expectedCountryGeolocationRequired) {
            setGpsState(false, true);
            setIsAccessGranted(true);
          } else {
            makeGet(`capture/${photoSeries.id}/capturing-allowed`).subscribe(({ response }: AjaxResponse<any>) => {
              setGpsState(false, true);
              if (response) {
                setIsAccessGranted(true);
              } else {
                dispatch(setServiceUnavailable(true));
              }
            });
          }
        });
      },

      // Error callback
      // eslint-disable-next-line no-undef
      (err: GeolocationPositionError) => {
        if (didRespond) return;
        finalize();

        const msg = `GeolocationPositionError; Message: ${err.message}; Code: ${err.code}; GPS Required: ${photoSeries.trackGeolocationRequired}`;
        handleFailure(msg);
      }
    );
  };

  const getCameraAndGPSAccess = async () => {
    setIsAccessGrantingInProgress(true);

    if (photoSeries.trackDeviceOrientationEnabled) {
      await requestGyroscopePermission();
    }

    if (useNativeCamera) {
      getGeolocationAccess();
    } else {
      getCameraAccess()
        .then((stream) => {
          const activeTracks = _.filter(stream.getVideoTracks(), (track) => track.enabled && track.readyState === 'live');

          if (!_.isEmpty(activeTracks)) {
            _.each(activeTracks, (videoTrack) => videoTrack.stop());
          }

          dispatch(setCameraAccessDenied(false));
          getGeolocationAccess();
        })
        .catch((err) => {
          logErrorToServer('Camera access error', err as CustomError);

          if (err && err.name === 'OverconstrainedError' && err.constraint === 'width') {
            setIsAccessGrantingInProgress(false);
            dispatch(storeUseNativeCamera(true));
            getGeolocationAccess();
          } else {
            setIsAccessGrantingInProgress(false);
            dispatch(setCameraAccessDenied(true));
          }
        });
    }
  };

  return {
    getCameraAndGPSAccess,
    isAccessGrantingInProgress,
    isAccessGranted
  };
};
